// @ts-check
// Cartão de um modelo do WhatsApp na tela de modelos (contrato 08 de leads e WhatsApp, seção 7): nome, situação com
// texto, categoria, idioma, número, prévia em balão com as variáveis destacadas, explicação da situação, motivo da
// recusa e excluir. Desde a 2.0.0 (contrato 09 de campanhas de WhatsApp, seção 2) também os selos de qualidade, de
// categoria, do botão "Parar de receber", de comunicado e de uso, os números da mesma conta e as ações da aba WhatsApp
// de Campanhas > Modelos.
import { html } from 'htm/preact';
import { StatusBadge } from '../integration-ui.js';
import { LightIconButton } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { formatDateTime, formatShortDate } from '../../../lib/format.js';
import { rejectedReasonDisplay, splitVariableSegments, templateBadges } from '../../../lib/whatsapp-templates.js';
import { hasTranslation, t } from '../../../i18n/index.js';

/** @typedef {import('../../../data/integrations.js').WhatsappTemplate} WhatsappTemplate */

/** @type {Intl.DisplayNames | null} */
let languageNames = null;

/**
 * "Português (Brasil)" para pt_BR; sem suporte do navegador, o código.
 * @param {string} code
 */
export function languageLabel(code) {
  try {
    languageNames ??= new Intl.DisplayNames(['pt-BR'], { type: 'language' });
    const name = languageNames.of(String(code).replace('_', '-'));
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : code;
  } catch {
    return code;
  }
}

/** @param {string} status */
export function templateStatusLabel(status) {
  const key = `leadCapture.templates.statuses.${status}`;
  return hasTranslation(key) ? t(key) : status;
}

/** @param {string | null} category */
export function templateCategoryLabel(category) {
  if (!category) return '';
  const key = `leadCapture.templates.categories.${category}`;
  return hasTranslation(key) ? t(key) : category;
}

/**
 * Texto com as variáveis em chips (prévia do cartão e do formulário).
 * @param {{ text: string | null | undefined, values?: Record<string, string> }} props
 */
export function TemplateText({ text, values = {} }) {
  return html`${splitVariableSegments(text).map((segment, index) => (segment.kind === 'variable'
    ? (values[segment.value]
      ? html`<span key=${index} class="template-variable" data-filled="true">${values[segment.value]}</span>`
      : html`<span key=${index} class="template-variable">{{${segment.value}}}</span>`)
    : html`<span key=${index}>${segment.value}</span>`))}`;
}

/**
 * Balão de mensagem com cabeçalho, corpo, rodapé e botões do modelo.
 * @param {{ headerText?: string | null, bodyText?: string | null, footerText?: string | null, buttons?: any[], values?: Record<string, string>, label?: string }} props
 */
export function TemplatePreviewBubble({ headerText, bodyText, footerText, buttons = [], values = {}, label }) {
  return html`<div class="template-bubble" role="group" aria-label=${label}>
    ${headerText ? html`<p class="template-bubble-header"><${TemplateText} text=${headerText} values=${values} /></p>` : null}
    <p class="template-bubble-body"><${TemplateText} text=${bodyText} values=${values} /></p>
    ${footerText ? html`<p class="template-bubble-footer">${footerText}</p>` : null}
    ${buttons.length > 0
      ? html`<div class="template-bubble-buttons">${buttons.map((button, index) => html`<span key=${index} class="template-bubble-button">${button?.text ?? ''}</span>`)}</div>`
      : null}
  </div>`;
}

/** Nome da categoria da Meta nos selos ("Marketing", "Utilidade"). @param {unknown} category */
function categoryName(category) {
  const key = `whatsappTemplates.categories.${String(category ?? '')}`;
  return hasTranslation(key) ? t(key) : String(category ?? '');
}

/**
 * Texto de um selo de templateBadges, com a categoria traduzida e a data curta.
 * @param {{ key: string, params?: Record<string, unknown> }} badge
 */
export function templateBadgeLabel(badge) {
  const params = badge.params ?? {};
  return t(`whatsappTemplates.${badge.key}`, {
    ...params,
    ...(params.category ? { category: categoryName(params.category) } : {}),
    ...(params.previous ? { previous: categoryName(params.previous) } : {}),
    ...(params.date ? { date: formatShortDate(/** @type {string} */ (params.date)) } : {}),
  });
}

/**
 * Selos do modelo (qualidade, categoria, botão de saída, comunicado e uso); `only` limita às chaves pedidas e `inline`
 * usa só spans (dentro de botão, como no seletor).
 * @param {{ template: WhatsappTemplate, only?: ReadonlySet<string> | null, inline?: boolean }} props
 */
export function TemplateBadges({ template, only = null, inline = false }) {
  const badges = templateBadges(template).filter((badge) => !only || only.has(badge.key));
  if (badges.length === 0) return null;
  if (inline) return html`<span class="wa-badges">${badges.map((badge) => html`<span key=${badge.key} class="wa-badge" data-tone=${badge.tone}>${templateBadgeLabel(badge)}</span>`)}</span>`;
  return html`<ul class="wa-badges">
    ${badges.map((badge) => html`<li key=${badge.key} class="wa-badge" data-tone=${badge.tone}>${templateBadgeLabel(badge)}</li>`)}
  </ul>`;
}

/**
 * @param {{ template: WhatsappTemplate, numberLabel: string, canManage: boolean, isHighlighted?: boolean, onDelete?: ((template: WhatsappTemplate) => void) | null,
 *   href?: string | null, numbersLabel?: string | null, actions?: any }} props
 *   href: o nome vira link para a página do modelo; numbersLabel: "Números: A, B" das cópias da mesma conta; actions: botões no pé
 */
export function WhatsappTemplateCard({ template, numberLabel, canManage, isHighlighted = false, onDelete = null, href = null, numbersLabel = null, actions = null }) {
  const updatedAt = template.syncedAt ?? template.updatedAt ?? null;
  const statusHelpKey = `whatsappTemplates.statusHelp.${template.status}`;
  const reason = rejectedReasonDisplay(template.rejectedReason);
  const hasMediaHeader = Boolean(template.headerFormat && template.headerFormat !== 'TEXT');
  return html`<li class="template-card" data-highlighted=${isHighlighted ? 'true' : undefined} data-status=${template.status} id=${`template-${template.id}`}>
    <div class="template-card-header">
      <div class="template-card-titles">
        ${href
          ? html`<a class="template-card-name template-card-name-link" href=${href} aria-label=${t('whatsappTemplates.campaignTab.openTemplate', { name: template.name })}>${template.name}</a>`
          : html`<span class="template-card-name">${template.name}</span>`}
        <span class="template-card-meta">
          <span>${templateCategoryLabel(template.category)}</span>
          <span aria-hidden="true">·</span>
          <span>${languageLabel(template.language)}</span>
          ${numberLabel ? html`<span aria-hidden="true">·</span><span>${numberLabel}</span>` : null}
        </span>
      </div>
      <${StatusBadge} status=${template.status} label=${templateStatusLabel(template.status)} />
      ${canManage && onDelete
        ? html`<${LightIconButton} icon="IconTrash" ariaLabel=${t('whatsappTemplates.actions.delete', { name: template.name })} onClick=${() => onDelete(template)} />`
        : null}
    </div>
    <${TemplateBadges} template=${template} />
    ${numbersLabel ? html`<p class="template-card-numbers">${numbersLabel}</p>` : null}
    <${TemplatePreviewBubble} headerText=${template.headerText} bodyText=${template.bodyText} footerText=${template.footerText} buttons=${template.buttons ?? []}
      label=${t('whatsappTemplates.card.preview', { name: template.name })} />
    ${hasTranslation(statusHelpKey) ? html`<p class="template-card-help"><${Icon} name="IconInfoCircle" size=${14} /><span>${t(statusHelpKey)}</span></p>` : null}
    ${template.status === 'REJECTED'
      ? html`<p class="template-card-rejected">
          <strong>${t('whatsappTemplates.card.rejectedReason')}</strong>
          <span>${t(reason.key, { code: reason.code })}</span>
          ${reason.showCode ? html`<code class="template-card-code">${reason.code}</code>` : null}
        </p>`
      : null}
    ${template.status === 'APPROVED' && !template.isSendable
      ? html`<p class="template-card-help"><${Icon} name=${hasMediaHeader ? 'IconPhoto' : 'IconInfoCircle'} size=${14} />
          <span>${hasMediaHeader ? t('whatsappTemplates.card.mediaHeader') : t('whatsappTemplates.notSendable')}</span></p>`
      : null}
    ${updatedAt ? html`<span class="integration-muted-small">${t('whatsappTemplates.card.updatedAt', { date: formatDateTime(updatedAt) })}</span>` : null}
    ${actions ? html`<div class="template-card-actions">${actions}</div>` : null}
  </li>`;
}
